import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Package,
  Search,
  ChevronRight,
  ChevronDown,
  Loader2,
  Phone,
  Mail,
  Send,
  CheckCircle,
  ExternalLink,
  Filter,
  X,
  Image as ImageIcon,
} from 'lucide-react';
import { fbEvents } from '@/lib/facebook-pixel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PublicAccessoiresPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [articles, setArticles] = useState([]);
  const [total, setTotal] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('categorie') || null);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [expandedCategories, setExpandedCategories] = useState({});
  const [page, setPage] = useState(0);
  const limit = 12;

  // Quote dialog
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [quoteForm, setQuoteForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    tonnage: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchArticles();
    // Update URL params
    const params = new URLSearchParams();
    if (selectedCategory) params.set('categorie', selectedCategory);
    if (searchQuery) params.set('q', searchQuery);
    setSearchParams(params);
  }, [selectedCategory, searchQuery, page]);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/api/public/accessoires/categories`);
      const data = await response.json();
      setCategories(data.categories || []);
      // Expand all by default
      const expanded = {};
      data.categories?.forEach(cat => {
        expanded[cat.id] = true;
      });
      setExpandedCategories(expanded);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCategory) params.append('category_slug', selectedCategory);
      if (searchQuery) params.append('search', searchQuery);
      params.append('skip', page * limit);
      params.append('limit', limit);

      const response = await fetch(`${API_URL}/api/public/accessoires/articles?${params.toString()}`);
      const data = await response.json();
      setArticles(data.articles || []);
      setTotal(data.total || 0);
    } catch (error) {
      console.error('Error fetching articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryClick = (slug) => {
    setSelectedCategory(selectedCategory === slug ? null : slug);
    setPage(0);
  };

  const toggleCategoryExpand = (catId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [catId]: !prev[catId]
    }));
  };

  const openQuoteDialog = (article) => {
    setSelectedArticle(article);
    setQuoteForm({
      name: '',
      email: '',
      phone: '',
      company: '',
      tonnage: article.compatible_tonnage || '',
      message: '',
    });
    setSubmitted(false);
    setQuoteDialogOpen(true);
    
    // Track Facebook ViewContent for accessory
    fbEvents.viewAccessory(article);
  };

  const handleSubmitQuote = async () => {
    if (!quoteForm.name || !quoteForm.email) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/public/accessoires/quote-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          article_id: selectedArticle.id,
          article_name: selectedArticle.name,
          ...quoteForm,
        }),
      });

      if (!response.ok) throw new Error('Erreur lors de l\'envoi');

      // Track Facebook Lead event for accessory quote
      fbEvents.requestAccessoryQuote(selectedArticle);

      setSubmitted(true);
      toast.success('Demande de devis envoyée !');
    } catch (error) {
      toast.error('Erreur lors de l\'envoi de la demande');
    } finally {
      setSubmitting(false);
    }
  };

  const formatPrice = (price, priceOnRequest) => {
    if (priceOnRequest || !price) return 'Sur demande';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(price) + ' HT';
  };

  const getCategoryName = (slug) => {
    const findCat = (cats) => {
      for (const cat of cats) {
        if (cat.slug === slug) return cat.name;
        if (cat.children) {
          const found = findCat(cat.children);
          if (found) return found;
        }
      }
      return null;
    };
    return findCat(categories) || slug;
  };

  const renderCategoryTree = (cats, level = 0) => {
    return cats.map(cat => {
      const hasChildren = cat.children && cat.children.length > 0;
      const isExpanded = expandedCategories[cat.id];
      const isSelected = selectedCategory === cat.slug;
      const hasArticles = cat.article_count > 0;

      return (
        <div key={cat.id}>
          <div
            className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-all ${
              isSelected 
                ? 'bg-orange-500 text-white' 
                : hasArticles 
                  ? 'hover:bg-orange-50 text-slate-700' 
                  : 'text-slate-400'
            }`}
            style={{ paddingLeft: `${12 + level * 16}px` }}
            onClick={() => hasArticles && handleCategoryClick(cat.slug)}
          >
            {hasChildren && (
              <button
                onClick={(e) => { e.stopPropagation(); toggleCategoryExpand(cat.id); }}
                className={`p-0.5 rounded ${isSelected ? 'hover:bg-orange-400' : 'hover:bg-slate-200'}`}
              >
                {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </button>
            )}
            {!hasChildren && <span className="w-5" />}
            <span className="flex-1 text-sm font-medium truncate">{cat.name}</span>
            {hasArticles && (
              <Badge variant={isSelected ? "secondary" : "outline"} className="text-xs">
                {cat.article_count}
              </Badge>
            )}
          </div>
          {hasChildren && isExpanded && renderCategoryTree(cat.children, level + 1)}
        </div>
      );
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white py-12">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
            Accessoires & Équipements
          </h1>
          <p className="text-slate-300 text-lg max-w-2xl">
            Découvrez notre gamme complète d'accessoires pour pelles et mini-pelles : 
            BRH, épareuses, grappins, tarières et plus encore.
          </p>
          
          {/* Search Bar */}
          <div className="mt-8 max-w-xl">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Rechercher un accessoire..."
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
                className="pl-12 h-12 bg-white text-slate-800 border-0 rounded-full shadow-lg"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Categories Sidebar */}
          <div className="lg:col-span-1">
            <Card className="sticky top-4">
              <CardContent className="p-4">
                <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <Filter className="w-5 h-5" />
                  Catégories
                </h3>
                
                <div
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-all mb-2 ${
                    !selectedCategory ? 'bg-orange-500 text-white' : 'hover:bg-orange-50 text-slate-700'
                  }`}
                  onClick={() => { setSelectedCategory(null); setPage(0); }}
                >
                  <Package className="w-4 h-4" />
                  <span className="text-sm font-medium">Tous les accessoires</span>
                </div>
                
                <div className="border-t my-3" />
                
                <div className="max-h-[500px] overflow-y-auto space-y-1">
                  {renderCategoryTree(categories)}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Articles Grid */}
          <div className="lg:col-span-3">
            {/* Results Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed' }}>
                  {selectedCategory ? getCategoryName(selectedCategory) : 'Tous les accessoires'}
                </h2>
                <p className="text-slate-500 mt-1">
                  {total} produit{total > 1 ? 's' : ''} {searchQuery && `pour "${searchQuery}"`}
                </p>
              </div>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-10 h-10 animate-spin text-orange-500" />
              </div>
            ) : articles.length === 0 ? (
              <div className="text-center py-20">
                <Package className="w-16 h-16 mx-auto text-slate-200 mb-4" />
                <p className="text-slate-500 text-lg">Aucun produit trouvé</p>
                {searchQuery && (
                  <Button 
                    variant="link" 
                    onClick={() => setSearchQuery('')}
                    className="mt-2 text-orange-500"
                  >
                    Effacer la recherche
                  </Button>
                )}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                  {articles.map((article) => (
                    <Card 
                      key={article.id} 
                      className="group overflow-hidden hover:shadow-xl transition-all duration-300 border-slate-100"
                    >
                      <div className="aspect-square bg-slate-100 relative overflow-hidden">
                        {article.image_url ? (
                          <img
                            src={article.image_url}
                            alt={article.name}
                            className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <ImageIcon className="w-16 h-16 text-slate-200" />
                          </div>
                        )}
                        {article.compatible_tonnage && (
                          <Badge className="absolute top-3 left-3 bg-slate-800">
                            {article.compatible_tonnage}
                          </Badge>
                        )}
                      </div>
                      
                      <CardContent className="p-4">
                        <Badge variant="outline" className="text-xs mb-2">
                          {article.category_name}
                        </Badge>
                        
                        <h3 className="font-semibold text-slate-800 line-clamp-2 min-h-[48px] mb-2">
                          {article.name}
                        </h3>
                        
                        {article.short_description && (
                          <p className="text-sm text-slate-500 line-clamp-2 mb-3">
                            {article.short_description}
                          </p>
                        )}
                        
                        <div className="flex items-end justify-between mt-auto pt-3 border-t">
                          <div>
                            {article.show_price === false ? (
                              <p className="text-sm text-slate-500 italic">Prix sur devis</p>
                            ) : (
                              <>
                                <p className="text-xs text-slate-400">Prix</p>
                                <p className={`text-xl font-bold ${article.selling_price ? 'text-green-600' : 'text-slate-600'}`}>
                                  {formatPrice(article.selling_price, article.price_on_request)}
                                </p>
                              </>
                            )}
                          </div>
                          <Button 
                            size="sm"
                            className="bg-orange-500 hover:bg-orange-600"
                            onClick={() => openQuoteDialog(article)}
                          >
                            <Send className="w-4 h-4 mr-1" />
                            Devis
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Pagination */}
                {total > limit && (
                  <div className="flex items-center justify-center gap-4 mt-10">
                    <Button
                      variant="outline"
                      disabled={page === 0}
                      onClick={() => setPage(p => p - 1)}
                    >
                      Précédent
                    </Button>
                    <span className="text-slate-600">
                      Page {page + 1} sur {Math.ceil(total / limit)}
                    </span>
                    <Button
                      variant="outline"
                      disabled={(page + 1) * limit >= total}
                      onClick={() => setPage(p => p + 1)}
                    >
                      Suivant
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Quote Dialog */}
      <Dialog open={quoteDialogOpen} onOpenChange={setQuoteDialogOpen}>
        <DialogContent className="max-w-lg">
          {submitted ? (
            <div className="text-center py-8">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <DialogTitle className="text-2xl mb-2">Demande envoyée !</DialogTitle>
              <DialogDescription className="text-base">
                Nous avons bien reçu votre demande de devis pour{' '}
                <strong>{selectedArticle?.name}</strong>. 
                Notre équipe vous contactera dans les plus brefs délais.
              </DialogDescription>
              <Button 
                className="mt-6 bg-orange-500 hover:bg-orange-600"
                onClick={() => setQuoteDialogOpen(false)}
              >
                Fermer
              </Button>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Demander un devis</DialogTitle>
                <DialogDescription>
                  Remplissez ce formulaire pour recevoir un devis personnalisé
                </DialogDescription>
              </DialogHeader>

              {/* Article Preview */}
              {selectedArticle && (
                <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg mb-4">
                  {selectedArticle.image_url && (
                    <img
                      src={selectedArticle.image_url}
                      alt={selectedArticle.name}
                      className="w-16 h-16 object-contain bg-white rounded"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-800 line-clamp-2">{selectedArticle.name}</p>
                    {selectedArticle.show_price !== false ? (
                      <p className="text-sm text-green-600 font-semibold">
                        {formatPrice(selectedArticle.selling_price, selectedArticle.price_on_request)}
                      </p>
                    ) : (
                      <p className="text-sm text-slate-500 italic">Prix sur devis</p>
                    )}
                  </div>
                </div>
              )}

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nom *</Label>
                    <Input
                      value={quoteForm.name}
                      onChange={(e) => setQuoteForm(f => ({ ...f, name: e.target.value }))}
                      placeholder="Votre nom"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={quoteForm.email}
                      onChange={(e) => setQuoteForm(f => ({ ...f, email: e.target.value }))}
                      placeholder="votre@email.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Téléphone</Label>
                    <Input
                      value={quoteForm.phone}
                      onChange={(e) => setQuoteForm(f => ({ ...f, phone: e.target.value }))}
                      placeholder="06 12 34 56 78"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Société</Label>
                    <Input
                      value={quoteForm.company}
                      onChange={(e) => setQuoteForm(f => ({ ...f, company: e.target.value }))}
                      placeholder="Nom de votre société"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Tonnage de votre machine</Label>
                  <Input
                    value={quoteForm.tonnage}
                    onChange={(e) => setQuoteForm(f => ({ ...f, tonnage: e.target.value }))}
                    placeholder="ex: 3.5 tonnes"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Message (optionnel)</Label>
                  <Textarea
                    value={quoteForm.message}
                    onChange={(e) => setQuoteForm(f => ({ ...f, message: e.target.value }))}
                    placeholder="Précisions sur votre demande..."
                    rows={3}
                  />
                </div>
              </div>

              <DialogFooter className="mt-6">
                <Button variant="outline" onClick={() => setQuoteDialogOpen(false)}>
                  Annuler
                </Button>
                <Button 
                  onClick={handleSubmitQuote} 
                  disabled={submitting}
                  className="bg-orange-500 hover:bg-orange-600"
                >
                  {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Envoyer la demande
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Contact CTA */}
      <div className="bg-slate-800 text-white py-12 mt-12">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
            Besoin de conseils ?
          </h2>
          <p className="text-slate-300 mb-6 max-w-xl mx-auto">
            Notre équipe technique est à votre disposition pour vous aider à choisir 
            l'équipement adapté à vos besoins.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="outline" className="border-white text-white hover:bg-white hover:text-slate-800">
              <Phone className="w-4 h-4 mr-2" />
              02 62 XX XX XX
            </Button>
            <Link to="/contact">
              <Button className="bg-orange-500 hover:bg-orange-600">
                <Mail className="w-4 h-4 mr-2" />
                Nous contacter
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicAccessoiresPage;
